import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Banknote, Hash } from 'lucide-react';
import apiClient from '@/services/api';
import type { PaymentsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/**
 * Tips — mirrors the POS Tips report: a flat list of individual tip records
 * (one per ticket with gratuity > 0), with a grand total. Excludes
 * voided/refunded tickets (the server feed already filters those out).
 */
export default function TipsReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<PaymentsReport>({
    queryKey: ['payments-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getPaymentsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const lines = data?.tipLines ?? [];
  const totalTips = lines.reduce((a, l) => a + Number(l.amount), 0);
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtTs = (s: string | null) => (s ? format(parseISO(s), 'MMM dd HH:mm') : '—');

  const sections = (): ReportSection[] => [
    {
      heading: 'Tips',
      headers: ['#', 'Ticket ID', 'Date', 'Amount'],
      rows: [
        ...lines.map((l, i) => [i + 1, l.ticketNumber ?? String(l.posTicketId ?? '—'), fmtTs(l.date), formatCurrency(l.amount)]),
        ['', '', 'TOTAL', formatCurrency(totalTips)],
      ],
    },
  ];
  const kpis = (): [string, string][] => [
    ['Total Tips', formatCurrency(totalTips)],
    ['Tips Count', lines.length.toLocaleString()],
  ];

  return (
    <ReportFrame
      title="Tips"
      description="Individual gratuity records in the range, with grand total (matches the POS Tips report)."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={lines.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`tips-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Tips', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Tips Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 gap-4">
        <KPICard label="Total Tips" value={formatCurrency(totalTips)} icon={Banknote} palette="green" />
        <KPICard label="Tips Count" value={lines.length.toLocaleString()} icon={Hash} palette="blue" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Tips</h3>
        <SimpleTable
          headers={['#', 'Ticket ID', 'Date', 'Amount']}
          rows={lines.map((l, i) => [i + 1, l.ticketNumber ?? String(l.posTicketId ?? '—'), fmtTs(l.date), formatCurrency(l.amount)])}
          footer={['', '', 'Total', formatCurrency(totalTips)]}
        />
      </div></div>
    </ReportFrame>
  );
}
